import React from 'react'
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { useSelector } from 'react-redux'
import { Key } from 'react'
import type { TimesheetStoreState } from '../../../../store/timesheetStore'

const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	webp: 'image/webp',
	pdf: 'application/pdf',
	txt: 'text/plain',
	csv: 'text/csv',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	zip: 'application/zip',
}

const EXTENSIONS_BY_MIME_TYPE: Record<string, string> = {
	'image/jpeg': 'jpg',
	'image/png': 'png',
	'image/gif': 'gif',
	'image/webp': 'webp',
	'application/pdf': 'pdf',
	'text/plain': 'txt',
	'text/csv': 'csv',
	'application/msword': 'doc',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
	'application/vnd.ms-excel': 'xls',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
	'application/zip': 'zip',
}

const DATA_URI_MIME_TYPE_REGEX = /^data:([^;]+);base64,/i

const getFileExtensionFromName = (fileName: string) => {
	return fileName
		.trim()
		.match(/\.([a-z0-9]+)$/i)?.[1]
		?.toLowerCase()
}

const getMimeTypeFromContent = (content: string) => {
	return content.match(DATA_URI_MIME_TYPE_REGEX)?.[1]?.toLowerCase()
}

const getBase64Content = (content: string) => {
	return content.replace(DATA_URI_MIME_TYPE_REGEX, '')
}

const detectExtensionFromBase64 = (content: string) => {
	const base64Content = getBase64Content(content).trim()

	if (base64Content.startsWith('/9j/')) return 'jpg'
	if (base64Content.startsWith('iVBORw0KGgo')) return 'png'
	if (base64Content.startsWith('R0lGOD')) return 'gif'
	if (base64Content.startsWith('UklGR')) return 'webp'
	if (base64Content.startsWith('JVBERi0')) return 'pdf'
	if (base64Content.startsWith('UEsDB')) return 'zip'

	return undefined
}

const getDownloadFile = (fileName: string, content: string) => {
	const mimeTypeFromContent = getMimeTypeFromContent(content)
	const extensionFromName = getFileExtensionFromName(fileName)
	const extension =
		extensionFromName ??
		(mimeTypeFromContent ? EXTENSIONS_BY_MIME_TYPE[mimeTypeFromContent] : undefined) ??
		detectExtensionFromBase64(content) ??
		'bin'
	const mimeType = mimeTypeFromContent ?? MIME_TYPES_BY_EXTENSION[extension] ?? 'application/octet-stream'
	const downloadBaseName = fileName.trim() || 'anexo'
	const downloadFileName = extensionFromName ? downloadBaseName : `${downloadBaseName}.${extension}`

	return {
		href: `data:${mimeType};base64,${getBase64Content(content).trim()}`,
		downloadFileName,
	}
}

function TemporaryImageDisplay() {
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	return (
		<Box
			sx={{
				paddingTop: 3,
				display: 'grid',
				gap: 1,
			}}
		>
			<Typography
				sx={{
					fontFamily: 'var(--font-family)',
					fontSize: '1rem',
					fontWeight: 600,
					lineHeight: '175%',
					color: 'var(--primary-dark)',
					letterSpacing: '0.15px',
				}}
			>
				Anexos
			</Typography>

			{timesheetPage?.documents ? (
				<Table
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '0.75rem',
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell
								colSpan={4}
								sx={{
									lineHeight: '200%',
									letterSpacing: '0.17px',
									color: 'var(--primary-light)',
									fontWeight: 600,
									padding: 2,
								}}
							>
								Anexo
							</TableCell>
							<TableCell
								sx={{
									padding: 2,
									lineHeight: '200%',
									letterSpacing: '0.17px',
									color: 'var(--primary-light)',
									fontWeight: 600,
									textAlign: 'right',
								}}
							>
								Imp / Exp
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{timesheetPage.documents.map((doc, index: Key) => {
							const downloadFile = getDownloadFile(doc.fileName, doc.content)

							return (
								<TableRow key={index}>
									<TableCell
										colSpan={4}
										sx={{
											border: 'none',
											padding: 2,
											lineHeight: '130%',
											letterSpacing: '0.17px',
											color: 'var(--primary-dark)',
											fontWeight: 400,
										}}
									>
										{doc.fileName}
									</TableCell>
									<TableCell
										sx={{
											textAlign: 'right',
											padding: '0 1rem',
											border: 'none',
										}}
									>
										<IconButton
											component='a'
											href={downloadFile.href}
											aria-label={`download ${downloadFile.downloadFileName}`}
											size='small'
											color='info'
											download={downloadFile.downloadFileName}
											sx={{
												marginBlock: 'auto',
											}}
										>
											<SaveAltIcon
												fontSize='small'
												sx={{ color: 'var(--primary)' }}
											/>
										</IconButton>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			) : (
				<Typography
					variant='subtitle2'
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '0.875rem',
						fontWeight: 600,
						lineHeight: '157%',
						color: 'var(--primary-light)',
					}}
				>
					Sem Anexos Adicionais
				</Typography>
			)}
		</Box>
	)
}

export default TemporaryImageDisplay
